import { Stack, SxProps } from '@mui/material';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { type Comment as BoardComment } from '@/hooks/apiHooks';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { ActionType } from '@/constant/button';
import CommentInput, {
  CommentInputRef,
} from '@/components/Comment/CommentInput';
import PopMenu from '@/components/PopMenu';
import PopMenuItem from '@/components/PopMenu/PopMenuItem';
import CommentList from '@/components/Comment/CommentList';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import {
  CommentListWrapper,
  HeaderTitle,
  SortButton,
  SortWrapper,
  Wrapper,
} from '@/components/Board/BoardDetail/Feedback/BoardDetailComment/styled';
import classNames from 'classnames';
import { t } from 'i18next';
import { ContentWithMentionsProps } from '@/components/Comment/CommentList/CommentItem/ContentWithMentions';
import { CommentMention } from '@/components/Comment/CommentInput/index.util';
import useBoardDetailCommentList from '@/components/Board/BoardDetail/Feedback/BoardDetailComment/useBoardDetailCommentList';
import useBoardDetailCommentCount from '@/components/Board/BoardDetail/Feedback/BoardDetailComment/useBoardDetailCommentCount';

export type BoardDetailCommentProps = {
  commentGroupId: string;
  title?: string;
  disabled?: boolean;
  onChangeCount?: (value: number) => void;
  handleComment?: (isComment: boolean, commentCount: number) => void;
  readonly sx?: SxProps;
  readonly inputStyle: 'top' | 'fixed' | 'mobile' | 'fixed-not-focus' | 'sheet';
  readonly service: 'board' | 'talkFlow' | 'marketing';
  /**
   * 코멘트 등록 전 게이트. `true` 면 등록 진행, `false` 면 중단.
   * 품의 영역에서 미권한자 자동 참조인 등록(employeeNumber/email 필요) 분기에 사용.
   */
  readonly beforeSubmit?: (
    mentionedIds: string[],
    mentions?: CommentMention[],
  ) => Promise<boolean>;
} & Pick<ContentWithMentionsProps, 'checkIsText'>;
export type BoardDetailCommentRef = CommentInputRef;

export const BoardDetailCommentID = 'BoardDetailComment';
export const BoardDetailCommentInputWrapperID =
  'board-detail-comment-input-wrapper';
const LIST_SIZE = 50;

const BoardDetailComment = forwardRef<
  BoardDetailCommentRef,
  BoardDetailCommentProps
>((props, ref) => {
  const {
    commentGroupId,
    title = '댓글',
    onChangeCount,
    handleComment,
    disabled,
    inputStyle,
    service,
    beforeSubmit,
    checkIsText,
    sx,
  } = props;

  const myInfo = useAtomValue(MyInfoState);

  const [currPage, setCurrPage] = useState(1);
  const { data, mutate, params, setParams } =
    useBoardDetailCommentList(commentGroupId);
  const { mutateCount } = useBoardDetailCommentCount(commentGroupId);

  const totalCount = data.length;

  const totalList = useMemo(() => {
    return data.map((item) => ({
      ...item,
      attachments: item.attachments ?? [],
      updatedAt: item.updatedAt || item.createdAt,
      isDeleted: Boolean(item.deletedAt && item.deletedAt),
      itUpdated: item.createdAt && item.updatedAt !== item.createdAt,
    }));
  }, [data]);

  const lastPage = Math.ceil(totalCount / LIST_SIZE);
  const hasNextPage = currPage < lastPage;
  const list = totalList.slice(0, currPage * LIST_SIZE);

  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const isSortMenuOpen = Boolean(sortMenuAnchorEl);

  const setOrder = (value: ActionType.SORT_RECENT | ActionType.SORT_OLD) => {
    let orderType: 'desc' | 'asc';
    switch (value) {
      case ActionType.SORT_OLD:
        orderType = 'asc';
        break;
      case ActionType.SORT_RECENT:
        orderType = 'desc';
        break;
    }

    if (orderType) {
      setParams((prev) => ({
        ...prev,
        orderType,
      }));

      setCurrPage(1);
      setSortMenuAnchorEl(null);
    }
  };

  useEffect(() => {
    onChangeCount?.(totalCount);
  }, [totalCount, onChangeCount]);

  const updateData = async () => {
    const updateData: BoardComment[] | undefined = await mutate();
    await mutateCount();

    if (updateData && updateData.length !== totalCount) {
      handleComment?.(
        data.filter((item) => item.createdBy.passportId === myInfo.passportId)
          .length > 0,
        updateData.length,
      );
    }
  };

  const CommentInputComponent = (
    <CommentInput
      ref={ref}
      commentGroupId={commentGroupId}
      updateCommentList={updateData}
      disabled={disabled}
      folding={true}
      inputStyle={inputStyle}
      service={service}
      beforeSubmit={beforeSubmit}
    />
  );

  const CommentListComponent = (
    <CommentList
      ref={ref}
      inputStyle={inputStyle}
      isEmpty={totalCount === 0}
      list={list}
      onChanged={updateData}
      isMobile={inputStyle === 'mobile'}
      hasNextPage={hasNextPage}
      nextPageButtonOnClick={() => {
        setCurrPage((curr) => curr + 1);
      }}
      service={service}
      leftCount={Math.min(LIST_SIZE, totalCount - currPage * LIST_SIZE)}
      beforeSubmit={beforeSubmit}
      checkIsText={checkIsText}
    />
  );

  return (
    <Wrapper
      id={BoardDetailCommentID}
      className={classNames({
        'top-style': inputStyle === 'top',
        'fixed-style': inputStyle === 'fixed',
        'mobile-style': inputStyle === 'mobile',
        'sheet-style': inputStyle === 'sheet',
      })}
      sx={sx}
    >
      {/* Header: rendered at top for all styles except 'sheet' (which renders header after input) */}
      {inputStyle !== 'sheet' && (
        <Stack className={`${BoardDetailCommentID}-header`} direction={'row'}>
          {inputStyle === 'top' || inputStyle === 'mobile' ? (
            <HeaderTitle>
              {t('코멘트')} {totalCount}
            </HeaderTitle>
          ) : (
            <Body1 sx={{ fontWeight: 700 }}>
              {title} {totalCount}
            </Body1>
          )}
          <SortWrapper>
            <SortButton
              aria-controls={isSortMenuOpen ? 'basic-menu' : undefined}
              aria-expanded={isSortMenuOpen ? 'true' : undefined}
              disabled={disabled}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setSortMenuAnchorEl(event.currentTarget);
              }}
            >
              {params.orderType === 'desc' ? t('최신순') : t('오래된순')}
            </SortButton>
            <PopMenu
              id={'comment-sort-menu'}
              anchorEl={sortMenuAnchorEl}
              open={isSortMenuOpen}
              onClose={() => setSortMenuAnchorEl(null)}
              MenuListProps={{ 'aria-labelledby': 'menu-button' }}
            >
              <PopMenuItem
                type={ActionType.SORT_RECENT}
                onClick={() => setOrder(ActionType.SORT_RECENT)}
                disabled={disabled}
              >
                {t('최신순')}
              </PopMenuItem>
              <PopMenuItem
                type={ActionType.SORT_OLD}
                onClick={() => setOrder(ActionType.SORT_OLD)}
                disabled={disabled}
              >
                {t('오래된순')}
              </PopMenuItem>
            </PopMenu>
          </SortWrapper>
        </Stack>
      )}
      {inputStyle === 'fixed' && (
        <>
          <CommentListWrapper className="comment-wrapper">
            {CommentListComponent}
          </CommentListWrapper>
          <Stack id="board-detail-comment-input-layout">
            <Stack id={BoardDetailCommentInputWrapperID}>
              {CommentInputComponent}
            </Stack>
          </Stack>
        </>
      )}
      {inputStyle === 'mobile' && (
        <>
          {CommentInputComponent}
          {CommentListComponent}
        </>
      )}
      {inputStyle === 'top' && (
        <>
          {CommentInputComponent}
          <Stack id={BoardDetailCommentInputWrapperID}>
            {CommentListComponent}
          </Stack>
        </>
      )}
      {inputStyle === 'sheet' && (
        <>
          {CommentInputComponent}
          <Stack className={`${BoardDetailCommentID}-header`} direction={'row'}>
            <HeaderTitle>
              {t('댓글')} {totalCount}
            </HeaderTitle>
            <SortWrapper>
              <SortButton
                aria-controls={isSortMenuOpen ? 'basic-menu' : undefined}
                aria-expanded={isSortMenuOpen ? 'true' : undefined}
                disabled={disabled}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  setSortMenuAnchorEl(event.currentTarget);
                }}
              >
                {params.orderType === 'desc' ? t('최신순') : t('오래된순')}
              </SortButton>
              <PopMenu
                id={'comment-sort-menu'}
                anchorEl={sortMenuAnchorEl}
                open={isSortMenuOpen}
                onClose={() => setSortMenuAnchorEl(null)}
                MenuListProps={{ 'aria-labelledby': 'menu-button' }}
              >
                <PopMenuItem
                  type={ActionType.SORT_RECENT}
                  onClick={() => setOrder(ActionType.SORT_RECENT)}
                  disabled={disabled}
                >
                  {t('최신순')}
                </PopMenuItem>
                <PopMenuItem
                  type={ActionType.SORT_OLD}
                  onClick={() => setOrder(ActionType.SORT_OLD)}
                  disabled={disabled}
                >
                  {t('오래된순')}
                </PopMenuItem>
              </PopMenu>
            </SortWrapper>
          </Stack>
          <Stack id={BoardDetailCommentInputWrapperID}>
            {CommentListComponent}
          </Stack>
        </>
      )}
    </Wrapper>
  );
});
BoardDetailComment.displayName = 'BoardDetailComment';

export default BoardDetailComment;
